import React, {createRef, Fragment, PureComponent} from "react"
import PropTypes from "prop-types"
import {compact, get, isNull, map, sample, sampleSize, uniq} from "lodash"
import {Grid} from "react-bootstrap"
import styled from "styled-components"
import {InjectedIntl} from "react-intl"

import withAuth, {WithAuth} from "@irati/auth/helpers/auth/withAuth"
import id from "@irati/utils/id"
import padding from "@irati/ui/src/global/padding"
import {FormattedMessage, injectIntl, InjectIntl} from "@irati/utils/lang"
import tracking from "@irati-tracking/perks/perks"
import HomePageQueryComponent from "@irati/deals/apollo/query/HomePage"
import LANE_TYPES from "@irati/deals/constants/laneTypes"
import {getBenefitsSchemeHeader, getCompanyBenefitsLogo} from "@irati/deals/helpers/deals/appearance"
import dealSettingsService from "@irati/deals/services/perksSettings"
import {getPrivateUserMetadata, updatePrivateUserMetadata} from "@irati/user-metadata/services/UserMetadata"
import reorderHomepageLanes from "@irati/deals/helpers/deals/reorderHomepageLanes"
import DealsFavourites from "@irati/deals/apollo/query/DealFavourites"
import favouritesService from "@irati/deals/services/favourites"
import {Deal, DealCategory, DealsTag, HomepageKind, HomepageLaneList, UserMetadata} from "@irati/types"
import {DEFAULT_CONFIG} from "@irati/user-metadata/constants/defaultPushConfig"
import {ButtonRouterLink} from "@irati/ui/src/Link"
import OnboardingModal, {OnboardingModalSteps} from "@irati/ui/src/OnboardingModal"
import onboardingService, {OnboardingStep} from "@irati/onboarding/services/onboarding"
import {walletTracking} from "@irati-tracking/wallet"
import Button from "@irati/ui/src/Button"
import Slider from "@irati/ui/src/Slider"
import {HeroSliderSettings} from "@irati/ui/src/Hero"
import Spacing from "@irati/ui/src/Spacing"
import isContentKey from "@irati/utils/lang/helpers/isContentKey"
import MySavingsCard from "@irati-web/my-savings/src/components/MySavingsCard"
import MySavingsCalculator from "@irati-web/my-savings/src/components/MySavingsCalculator"

import FeedSliderGraph from "../../components/FeedSlider"
import SectionHeader from "../../components/SectionHeader"
import TopCategories from "../../components/TopCategories"
import CustomisationBanner from "../../components/CustomisationBanner"
import CustomCategories from "../../components/CustomCategories"
import step1image from "../../images/wallet_onboarding_01.svg"
import step2image from "../../images/wallet_onboarding_02.svg"
import step3image from "../../images/wallet_onboarding_03.svg"
import DealSearch from "../../components/DealSearch"
import HeroPerk from "../../components/HeroPerk"
import DealPages from "../../enums/DealPages"
import {StyledHr} from "./styled/HomePage"

const StyledTopCategories = styled(TopCategories)`
  padding-bottom: calc(${padding.base} - 10px);
`

const CUSTOMISATION_OPTIONS_TO_DISPLAY = 3

interface Props extends InjectIntl, WithAuth {
  intl: InjectedIntl
}

interface State {
  companyBenefitsTopOfHomepage: boolean
  metadata: UserMetadata | null
  hasFetchedMetadata: boolean
  showFPOnboardingModal: boolean
}

interface RenderLaneProps {
  type: string
  tag: DealsTag
  deals: Deal[]
  headerText: string
  bodyText: string
  idNamespace: string
  index: number
  image: string
  seeMoreUrl: string
}

class HomePage extends PureComponent<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state: State = {
    companyBenefitsTopOfHomepage: false,
    metadata: null,
    hasFetchedMetadata: false,
    showFPOnboardingModal: false
  }

  componentDidMount() {
    const {
      auth: {user, productPermissions}
    } = this.props
    // force an update to favourites on load of page
    favouritesService.list({userId: user.uuid!}, {fetchPolicy: "network-only"})

    this.getSettings()
    this.getUserMetadata()

    tracking.viewHome()

    onboardingService.get(user.uuid!).then((userOnboarding) => {
      const hasFlexiPointsWallet = productPermissions.wallet?.treatTokens
      const hasSeenFPOnboardingModal = !!userOnboarding.userProgress?.find(
        ({step}) => step === OnboardingStep.HAS_VIEWED_FLEXI_POINTS_ONBOARDING_RETRIGGERED
      )
      const showFPOnboardingModal = !!(hasFlexiPointsWallet && !hasSeenFPOnboardingModal)

      this.setState({showFPOnboardingModal})
    })
  }

  getSettings = () => {
    const {
      auth: {userTenant}
    } = this.props
    const {uuid} = userTenant

    dealSettingsService.get(uuid).then((dealSettings) => {
      const companyBenefitsTopOfHomepage = get(dealSettings, "companyBenefits.topOfHomepage", false)
      this.setState({companyBenefitsTopOfHomepage})
    })
  }

  getUserMetadata = () => {
    const {
      auth: {user}
    } = this.props
    const {uuid} = user
    return getPrivateUserMetadata(uuid, {forceRefetch: true}).then((metadata) => {
      this.setState({metadata, hasFetchedMetadata: true})
    })
  }

  removeUnavailableCategories = (unavailableCategories: DealsTag[]) => {
    const {
      auth: {user}
    } = this.props
    const {uuid: userUuid} = user
    const {metadata} = this.state
    const config = metadata?.config || DEFAULT_CONFIG
    const unavailableCategoryIds = map(unavailableCategories, "uuid")
    const newFavouriteCategories = get(metadata, "config.favouriteCategories", []).filter(
      ({uuid}: DealsTag) => !unavailableCategoryIds.includes(uuid)
    )

    return updatePrivateUserMetadata({
      ...metadata,
      config: {
        ...config,
        favouriteCategoryIds: map(newFavouriteCategories, "uuid")
      },
      uuid: userUuid
    }).then(() => {
      this.getUserMetadata()
    })
  }

  getHeaderName(type: string, tag?: DealsTag, customHeaderName?: string) {
    const {
      formatMessage,
      auth: {userTenant}
    } = this.props
    const benefitSchemeHeader = getBenefitsSchemeHeader()

    switch (type) {
      case LANE_TYPES.FAVOURITE:
        return formatMessage({id: "perks.containers.homepage.favourites"})
      case LANE_TYPES.COMPANY:
        return benefitSchemeHeader || formatMessage({id: "perks.containers.homepage.benefits"}, {name: userTenant.public_name})
      case LANE_TYPES.NEW:
        return formatMessage({id: "perks.containers.homepage.newPerks"})
      case LANE_TYPES.TRENDING:
        return formatMessage({id: "perks.containers.homepage.trending"})
      case LANE_TYPES.TAG:
        return tag ? tag.name : null
      default:
        if (isContentKey(customHeaderName)) {
          return formatMessage({id: customHeaderName})
        }
        return customHeaderName
    }
  }

  renderSlider = ({type, tag, deals, headerText, bodyText, seeMoreUrl, idNamespace}: Partial<RenderLaneProps>) => {
    const {
      auth: {branding}
    } = this.props
    const companyBenefitsLogo = getCompanyBenefitsLogo(branding)
    const sectionHeaderText = this.getHeaderName(type, tag, headerText)

    return (
      <FeedSliderGraph
        data={deals}
        sectionHeaderText={sectionHeaderText}
        showSectionHeader={!!sectionHeaderText}
        seeMoreUrl={seeMoreUrl}
        sectionBodyText={bodyText}
        id={id(type, idNamespace)}
        logo={type === LANE_TYPES.COMPANY ? companyBenefitsLogo : null}
        page={DealPages.HOME}
      />
    )
  }

  renderLane = ({type, tag, deals, headerText, bodyText, seeMoreUrl, idNamespace, image, index}: RenderLaneProps) => {
    let Component
    let key

    if (deals.length > 0) {
      key = tag ? tag.uuid : index
      Component = this.renderSlider({type, tag, deals, headerText, bodyText, idNamespace, image, seeMoreUrl, index})
    }

    if (Component) {
      return (
        <Fragment key={key || index}>
          <StyledHr />
          {Component}
        </Fragment>
      )
    }

    return null
  }

  orderHomepageLanes = (homepageLanes: HomepageLaneList, idNamespace: string) => {
    return reorderHomepageLanes(homepageLanes.results, this.state.companyBenefitsTopOfHomepage).map(
      ({type, tag, deals, headerText, bodyText, image, seeMoreUrl}, index) =>
        this.renderLane({type, tag, deals, headerText, bodyText, idNamespace, image, seeMoreUrl, index})
    )
  }

  getBannerSuggestions = (homepageDealCategories: DealCategory[]) => {
    const randomNames = compact(homepageDealCategories)
      .filter(({active, type}) => active === true && isNull(type))
      .map(({tags}) => sample(tags)?.name)
    return sampleSize(compact(uniq(randomNames)), CUSTOMISATION_OPTIONS_TO_DISPLAY)
  }

  onboardingModalRef = createRef()
  onboardingModalSettings = () => {
    const {
      auth: {user}
    } = this.props
    const {apps: WALLET} = this.context
    return [
      {
        image: step1image,
        title: "perks.onboarding.step1.title",
        description: "perks.onboarding.step1.description"
      },
      {
        image: step2image,
        title: "perks.onboarding.step2.title",
        description: "perks.onboarding.step2.description"
      },
      {
        image: step3image,
        title: "perks.onboarding.step3.title",
        description: "perks.onboarding.step3.description",
        actions: [
          <ButtonRouterLink
            marginRight
            app={WALLET}
            onClick={() => {
              onboardingService.completeStep(user, OnboardingStep.HAS_VIEWED_FLEXI_POINTS_ONBOARDING_RETRIGGERED)
              walletTracking.clickGoToMyWallet()
              this.onboardingModalRef.current.hideModal()
            }}
            to="/">
            <FormattedMessage id="perks.onboarding.finalStep.buttons.goToMyWallet" />
          </ButtonRouterLink>,
          <Button
            primary
            onClick={() => {
              onboardingService.completeStep(user, OnboardingStep.HAS_VIEWED_FLEXI_POINTS_ONBOARDING_RETRIGGERED)
              walletTracking.clickDone(user.businessId)
              this.onboardingModalRef.current.hideModal()
            }}>
            <FormattedMessage id="perks.onboarding.finalStep.buttons.done" />
          </Button>
        ]
      }
    ] as OnboardingModalSteps[]
  }

  render() {
    const {formatMessage, auth} = this.props
    const {user} = auth
    const idNamespace = id("homepage", this.props, {force: true})
    /**  containers should force to init the idNamespace */

    const {metadata, hasFetchedMetadata, showFPOnboardingModal} = this.state

    return (
      <HomePageQueryComponent variables={{kind: HomepageKind.KindPerks}}>
        {({data: {homepageBanners, homepageDealCategories, homepageLanes}}) => (
          <Grid>
              <DealSearch />
              <Spacing marginTop={2} />
              <MySavingsCard ctaType="goToSavings" />
              <Spacing marginTop={3} />
              <Slider slidesToShow={1} settings={HeroSliderSettings}>
                {homepageBanners?.map((deal, i) => (
                  <HeroPerk
                    key={deal.uuid}
                    deal={deal}
                    onClick={() => tracking.clickPerk(deal, {sectionHeader: "hero", position: i + 1, source: "homepage"})}
                  />
                ))}
              </Slider>
              <MySavingsCalculator />

              {user && hasFetchedMetadata && (
                <CustomisationBanner
                  user={user}
                  metadata={metadata}
                  suggestions={this.getBannerSuggestions(homepageDealCategories?.results)}
                />
              )}

              {metadata && (
                <CustomCategories
                  idNamespace={id("custom-categories", idNamespace)}
                  metadata={metadata}
                  removeUnavailableCategories={this.removeUnavailableCategories}
                />
              )}

              <SectionHeader
                id={id("categories", idNamespace)}
                linkLabel="perks.containers.homepage.topCategories.label"
                linkTo="/categories"
                onClick={tracking.clickBrowseByCategory}>
                <FormattedMessage id="perks.containers.homepage.topCategories.title" />
              </SectionHeader>

              {homepageDealCategories && <StyledTopCategories id={idNamespace} categories={homepageDealCategories.results} />}

              <DealsFavourites variables={{userId: user.uuid}}>
                {({data}) => (
                  <FeedSliderGraph
                    id={id("favourites", idNamespace)}
                    data={data?.dealFavourites}
                    sectionHeaderText={this.getHeaderName(LANE_TYPES.FAVOURITE)}
                    showSectionHeader={!!data?.dealFavourites?.length}
                    isFavouriteDeal
                    page={DealPages.HOME}
                  />
                )}
              </DealsFavourites>

              {homepageLanes && this.orderHomepageLanes(homepageLanes, idNamespace)}

              {showFPOnboardingModal && <OnboardingModal steps={this.onboardingModalSettings()} ref={this.onboardingModalRef} />}
            </Grid>
        )}
      </HomePageQueryComponent>
    )
  }
}

export default injectIntl(withAuth(HomePage))
